import _ from 'lodash';
import { createSelector } from 'reselect';

import RefData from 'ref-data';

import { sharingNetworkFields, sharingNetworks, preDesignedApplications } from '../../common/enums';
import UserSelectors from '../user/user-selectors';
import Theme from '../theme';

//TODO, _.get here is temporary because of the reliance of getCompatibleExperience being called when this isn't initialized
const _scopedState = (state) => _.get(state, 'entities.experience', {});

const experiencesById = (state) => {
  return _scopedState(state).byId;
};

const isFetching = (state) => _scopedState(state).isFetching;
const isEmpty = createSelector([experiencesById], (experiences) => _.isEmpty(experiences));

const metrics = (state) => _scopedState(state).metrics;

const experiences = createSelector([experiencesById], (experiences) => {
  return _.values(experiences);
});

const experienceById = createSelector(
  [experiencesById, (state, experienceId) => experienceId],
  (experiences, experienceId) => experiences[experienceId]
);

const sortedExperiences = createSelector(
  [experiences, (state, sort) => sort, (state, sort, dir = 'desc') => dir],
  (experiences, sort, dir) => {
    return _.orderBy(experiences, sort, dir);
  }
);

const experience = (state) => {
  return _scopedState(state).activeExperience;
};

const theme = (state) => {
  return experience(state).themeId ? Theme.Selectors.themeById(state, experience(state).themeId) : {};
};

const isConfirmDeleteExperienceModalVisible = (state) => _scopedState(state).isConfirmDeleteExperienceModalVisible;
const isResetExperienceDataModalVisible = (state) => _scopedState(state).isResetExperienceDataModalVisible;
const isConfirmExpireExperienceModalVisible = (state) => _scopedState(state).isConfirmExpireExperienceModalVisible;
const isConfirmUpdateMetadataModalVisible = (state) => _scopedState(state).isConfirmUpdateExperienceModalVisible;
const updateExternalRefFromExperienceMetadata = (state) => _scopedState(state).updateExternalRefFromExperienceMetadata;

const experienceMetadata = (state) => experience(state).experienceMetadata;
const experienceMetadataFieldsFiltered = createSelector([experienceMetadata], (experienceMetadata) =>
  // _.get is used to avoid an error. When a macro experience settings page is updates,
  // fieldsByVisibility is empty until the api call returns with the full experienceMetadata config.
  experienceMetadata.fields.filter((field) => _.get(experienceMetadata, ['fieldsByVisibility', 'edit', field.name]))
);

const dirtyFields = (state) => _scopedState(state).dirtyFields;

const isExperienceDirty = createSelector([dirtyFields], (dirtyFields) => !_.isEmpty(dirtyFields));

// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
// Sharing config.

const _sharingConfig = createSelector([experience], (experience) => experience.sharingConfig);

const _defaultSharingConfig = createSelector([_sharingConfig], (sharingConfig) => {
  return sharingConfig[sharingNetworks.DEFAULT];
});

const sharingConfigByNetwork = createSelector(
  [_sharingConfig, (state, network) => network],
  (sharingConfig, network) => {
    return sharingConfig[network];
  }
);
//Header config is using a layout json config similar to pages, but the only editable part from it is the data from the first module
const headerConfigData = createSelector([experience], (experience) =>
  _.get(experience, 'headerConfig.modules[0].data', {})
);

const isSharingConfigNetworkEnabled = createSelector(
  [sharingConfigByNetwork],
  (sharingConfig) => sharingConfig.enabled
);

const isSharingConfigNetworkCustomized = createSelector(
  [sharingConfigByNetwork, _defaultSharingConfig, (state, network) => network],
  (networkSharingConfig, defaultSharingConfig, network) => {
    const _pickedDefaultData = _.pick(defaultSharingConfig.data, sharingNetworkFields[network]);
    const _pickedNetworkData = _.pick(networkSharingConfig.data, sharingNetworkFields[network]);

    // if the cut down field set doesn't match the default config, then it has been customized.
    return !_.isEqual(_pickedDefaultData, _pickedNetworkData);
  }
);

const _experienceStatusRef = (state) => experience(state).status.id;

const _storeApplicationFriendlyId = (state) => experience(state).storeApplicationFriendlyId;

const isExperienceDeprecated = (state) => experience(state).isDeprecated;

const isDraftReadyScheduled = createSelector(_experienceStatusRef, (experienceStatusRef) =>
  [
    RefData.Enum.STATUS.CAMPAIGNOBJECT.DRAFT,
    RefData.Enum.STATUS.CAMPAIGNOBJECT.READY,
    RefData.Enum.STATUS.CAMPAIGNOBJECT.SCHEDULED,
  ].includes(experienceStatusRef)
);

const allowExperienceUidEditing = createSelector(_experienceStatusRef, (experienceStatusRef) =>
  [
    RefData.Enum.STATUS.CAMPAIGNOBJECT.DRAFT,
    RefData.Enum.STATUS.CAMPAIGNOBJECT.READY,
    RefData.Enum.STATUS.CAMPAIGNOBJECT.TEMPLATE,
  ].includes(experienceStatusRef)
);

const isDraftExpiredTemplate = createSelector(_experienceStatusRef, (experienceStatusRef) =>
  [
    RefData.Enum.STATUS.CAMPAIGNOBJECT.DRAFT,
    RefData.Enum.STATUS.CAMPAIGNOBJECT.EXPIRED,
    RefData.Enum.STATUS.CAMPAIGNOBJECT.TEMPLATE,
  ].includes(experienceStatusRef)
);

const isPreDesignedApplication = createSelector(_storeApplicationFriendlyId, (storeApplicationFriendlyId) =>
  preDesignedApplications.ALL.includes(storeApplicationFriendlyId)
);

const customValue = createSelector(experience, (experience) => {
  if (!experience.customScript && !experience.customStyles) {
    return 'None';
  }
  if (experience.customScript && experience.customStyles) {
    return 'Custom CSS and Custom Javascript';
  }
  return experience.customScript ? 'Custom Javascript' : 'Custom CSS';
});

const isTwitterEnabled = createSelector(experience, (experience) => {
  if (!experience.isInNetwork) {
    return false;
  }
  return experience.innetworkConfig.twitter ? experience.innetworkConfig.twitter.enabled : false;
});

const twitterEntryFormat = createSelector(experience, (experience) => {
  if (!experience.isInNetwork) {
    return '';
  }
  if (experience.innetworkConfig.twitter) {
    const hashtag = experience.innetworkConfig.twitter.hashtag || '';
    const mention = experience.innetworkConfig.twitter.mention || '@{YourAccountName}';
    return hashtag + ' and ' + mention;
  }
  return '';
});

const isInstagramEnabled = createSelector(experience, (experience) => {
  if (!experience.isInNetwork) {
    return false;
  }
  return experience.innetworkConfig.instagram ? experience.innetworkConfig.instagram.enabled : false;
});

const instagramEntryFormat = createSelector(experience, (experience) => {
  if (!experience.isInNetwork) {
    return '';
  }
  return experience.innetworkConfig.instagram ? experience.innetworkConfig.instagram.hashtag : '';
});

const isPublishedToInstagram = createSelector(
  [experience],
  (experience) =>
    !_.some(experience.displayContainers, ['typeRef', 'id', RefData.Enum.TYPE.DISPLAYCONTAINER_INSTAGRAM.INNETWORK])
);

const quotaStatus = (state) => _scopedState(state).quotaStatus;

const quotaStatusMessage = createSelector(quotaStatus, (quotaStatus) =>
  _.get(quotaStatus, 'statusMessages[0].statusMessage', '')
);

const showNetworkStatusSection = createSelector(
  experience,
  (experience) => !experience.isExpired && experience.isInNetwork
);

const showPublishToSection = createSelector(
  [isExperienceDeprecated, UserSelectors.isPublisher],
  (isDeprecated, userIsPublisher) => !isDeprecated && userIsPublisher
);

export default {
  metrics,
  experience,
  experiences,
  experienceById,
  isFetching,
  isEmpty,
  sortedExperiences,
  theme,
  isConfirmDeleteExperienceModalVisible,
  isResetExperienceDataModalVisible,
  isConfirmExpireExperienceModalVisible,
  isConfirmUpdateMetadataModalVisible,
  updateExternalRefFromExperienceMetadata,
  experienceMetadata,
  experienceMetadataFieldsFiltered,
  experiencesById,
  dirtyFields,
  isExperienceDirty,
  headerConfigData,
  sharingConfigByNetwork,
  isSharingConfigNetworkCustomized,
  isSharingConfigNetworkEnabled,
  isExperienceDeprecated,
  isDraftExpiredTemplate,
  isDraftReadyScheduled,
  allowExperienceUidEditing,
  customValue,
  isTwitterEnabled,
  twitterEntryFormat,
  instagramEntryFormat,
  isInstagramEnabled,
  isPublishedToInstagram,
  isPreDesignedApplication,
  quotaStatusMessage,
  showNetworkStatusSection,
  showPublishToSection,
};
